/**
 * Archies — PDP "please select a size" gate.
 *
 * The PDP loads with NO size pre-selected unless the URL carries an explicit ?variant= (ad/deep link)
 * or a remembered size is re-applied from localStorage (see tabs-ext.js). While no size is selected the
 * Add-to-cart button is gated: it shows "Please select a size", is aria-disabled (kept focusable, NOT
 * `disabled`, so keyboard/AT users can reach it) and non-submitting; tapping it moves focus to the size
 * picker and announces the prompt. The instant a size is chosen the button returns to "Add to cart".
 *
 * Server renders the initial gated state (add-to-cart-button-ext.liquid, keyed on product.selected_variant)
 * so there's no flash; this script keeps it in sync as the shopper selects a size, as localStorage
 * re-selects a remembered size on load, and after a combined-listing colour swap re-renders <main>.
 *
 * [Archies] SCOPE-AWARE: the main PDP and the quick-add popover can each have their OWN picker + gated
 * ATC in the page at the same time. Everything below operates within a SCOPE (the popover modal
 * `#quick-add-modal-content`, else the product `.shopify-section`, else document) rather than
 * document-first — otherwise selecting a size in the popover would target the PDP's button and the
 * popover's own button would never un-grey.
 */
(function () {
  'use strict';

  var FORM_SEL = '.variant-picker__form-ext';
  // A checked SIZE option. Match on data-size-value (present only on size radios) — NOT
  // data-option-value-id, which the combined-listing colour swatch also carries and is always checked
  // for the current product's colour (that would wrongly read as "a size is selected").
  var CHECKED_SIZE_SEL = 'input[data-size-value]:checked';

  // The nearest self-contained product context for a node: the quick-add popover, else the product
  // section, else the whole document.
  function scopeFor(node) {
    if (node && node.closest) {
      return node.closest('#quick-add-modal-content') || node.closest('.shopify-section') || document;
    }
    return document;
  }

  function sizeSelected(scope) {
    scope = scope || document;
    var form = scope.querySelector(FORM_SEL);
    if (!form) return true; // no picker in this scope → never gate
    // Footwear: gate on the SIZE option (data-size-value). Only size radios carry it, so a
    // combined-listing colour swatch (always checked for the current colour) can't false-positive.
    if (form.querySelector('input[data-size-value]')) {
      return !!form.querySelector(CHECKED_SIZE_SEL);
    }
    // No size option (e.g. a gift card's Denominations) → gate on any selected variant option.
    if (!form.querySelector('input[data-option-value-id]')) return true; // no options → never gate
    return !!form.querySelector('input[data-option-value-id]:checked');
  }

  function atcButton(scope) {
    scope = scope || document;
    var comp = scope.querySelector('add-to-cart-component');
    return comp ? comp.querySelector('button') : null;
  }

  // Reflect the current selection state onto the ATC button (class + ARIA) within a scope. Idempotent.
  function updateGate(scope) {
    scope = scope || document;
    var comp = scope.querySelector('add-to-cart-component');
    if (!comp) return;
    var btn = comp.querySelector('button');
    if (!btn) return;

    var needs = !sizeSelected(scope);
    btn.classList.toggle('atc--needs-size', needs);
    comp.setAttribute('data-needs-size', needs ? 'true' : 'false');

    if (needs) {
      btn.setAttribute('aria-disabled', 'true');
      var label = comp.getAttribute('data-select-size-text');
      if (label) btn.setAttribute('aria-label', label);
      // Horizon delegates `on:click="/handleClick"` from a document-level CAPTURE listener registered
      // before ours (component.js), so it fires the add-to-cart animation before our guard can stop the
      // event. It reads the on:click attribute per-dispatch, so removing the attribute while gated stops
      // handleClick entirely (no misleading "Added" tick). Stashed + restored when a size is chosen.
      if (btn.hasAttribute('on:click')) {
        btn.setAttribute('data-atc-onclick', btn.getAttribute('on:click'));
        btn.removeAttribute('on:click');
      }
      // Also drop it out of the submit flow entirely (belt-and-braces alongside the capture guard).
      btn.setAttribute('type', 'button');
    } else {
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-label');
      if (btn.hasAttribute('data-atc-onclick')) {
        btn.setAttribute('on:click', btn.getAttribute('data-atc-onclick'));
        btn.removeAttribute('data-atc-onclick');
      }
      btn.setAttribute('type', 'submit');
    }
  }

  // Update every ATC on the page, each within its own scope (PDP + any open popover).
  function updateAllGates() {
    var comps = document.querySelectorAll('add-to-cart-component');
    if (!comps.length) { updateGate(document); return; }
    comps.forEach(function (comp) { updateGate(scopeFor(comp)); });
  }

  // Move focus + view to the size picker and announce the prompt (used when the gated ATC is tapped).
  function nudgeToSize(scope) {
    scope = scope || document;
    var form = scope.querySelector(FORM_SEL);
    if (!form) return;

    // Prefer the first visible (active-gender) size option; fall back to its fieldset.
    var options = Array.prototype.slice.call(form.querySelectorAll('input[data-size-value]'));
    var firstVisible = options.filter(function (i) { return i.offsetParent !== null; })[0];
    var scrollTarget = firstVisible ? (firstVisible.closest('fieldset') || firstVisible) : form;

    // [Archies a11y G3] no smooth scroll for shoppers who ask for reduced motion
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { scrollTarget.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); } catch (e) { scrollTarget.scrollIntoView(); }
    if (firstVisible) {
      // Focus the label (radios are visually buttons); the input is focusable and drives :focus-visible.
      try { firstVisible.focus({ preventScroll: true }); } catch (e) { firstVisible.focus(); }
    }

    announce(scope);
  }

  // Announce via the product form's assertive live region (falls back to a transient one).
  function announce(scope) {
    scope = scope || document;
    var comp = scope.querySelector('add-to-cart-component');
    var msg = (comp && comp.getAttribute('data-select-size-text')) || 'Please select a size';
    var region = scope.querySelector('product-form-component [ref="liveRegion"]')
      || document.querySelector('product-form-component [ref="liveRegion"]');
    if (region) {
      region.textContent = '';
      // Re-set on next frame so repeat taps re-announce.
      requestAnimationFrame(function () { region.textContent = msg; });
    }
  }

  // Capture-phase guard: intercept a tap on the gated button BEFORE its /handleClick + the form submit,
  // so no accidental add happens; nudge instead. Enter/Space on the button dispatch a click, so this
  // covers keyboard activation too.
  function onCaptureClick(event) {
    var t = event.target;
    var btn = t && t.closest ? t.closest('add-to-cart-component button') : null;
    if (!btn || !btn.classList.contains('atc--needs-size')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    nudgeToSize(scopeFor(btn));
  }

  // Belt-and-braces: block the product form submit while gated (in case a submit is triggered without a
  // button click).
  function onCaptureSubmit(event) {
    var scope = scopeFor(event.target);
    var btn = atcButton(scope);
    if (btn && btn.classList.contains('atc--needs-size')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      nudgeToSize(scope);
    }
  }

  // A combined-listing colour swatch. Swapping colour morphs <main> to the target product.
  var SWATCH_SEL = '.swatch-group__swatches label, .swatch-groups-wrapper label';

  // Whether the shopper had NO size selected at the moment they clicked a colour swatch. The swatch's
  // connected-product-url carries a default ?variant=, so the morph would otherwise auto-select a size on
  // the new colour — the exact accidental-wrong-size case we're preventing. If they hadn't chosen a size,
  // we undo that selection after the morph so the new colour also loads unselected + gated.
  var undoSizeOnNextMorph = false;

  function deselectAllSizes(scope) {
    scope = scope || document;
    var form = scope.querySelector(FORM_SEL);
    if (!form) return;
    form.querySelectorAll('input[data-size-value]').forEach(function (i) {
      i.checked = false;
      i.removeAttribute('checked');
    });
  }

  // Keep the URL honest: with no size selected there should be no ?variant= (so a reload/share stays
  // unselected). An explicit ad ?variant= only ever preselects on first load, which is intended.
  function stripVariantParam() {
    try {
      var url = new URL(window.location.href);
      if (url.searchParams.has('variant')) {
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    } catch (e) { /* no-op */ }
  }

  function init() {
    updateAllGates();
    // A size selection (or localStorage re-select) fires `change` in the picker — gate the ATC in the
    // SAME scope (so a popover selection updates the popover's button, not the PDP's).
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest(FORM_SEL)) updateGate(scopeFor(e.target));
    });
    // Record the pre-swap selection state the moment a colour swatch is clicked (capture, before the
    // picker fetches the connected product). The popover has no swatches, so this only ever fires on PDP.
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest(SWATCH_SEL)) {
        undoSizeOnNextMorph = !sizeSelected(scopeFor(e.target));
      }
    }, true);
    // Combined-listing colour swap re-renders <main>; re-derive the gate against the fresh DOM, and if the
    // shopper hadn't chosen a size before the swap, strip the swatch's default variant back out.
    window.addEventListener('variantHtmlUpdatedCustomFlag', function () {
      var undo = undoSizeOnNextMorph;
      undoSizeOnNextMorph = false;
      var apply = function () {
        if (undo) deselectAllSizes(document);
        updateAllGates();
      };
      if (undo) stripVariantParam();
      apply();
      setTimeout(apply, 60);
    });
    document.addEventListener('click', onCaptureClick, true);
    document.addEventListener('submit', onCaptureSubmit, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
